import { useCallback, useMemo, useState } from 'react';
import { FileDown } from 'lucide-react';
import CalendrierAnnuel from './calendrier/CalendrierAnnuel';
import {
  TypeJour,
  JourCalendrierLocal,
  ORDRE_TYPES,
  STYLES_JOUR,
  appliquerJoursFeries,
  appliquerRythmeAlternance,
} from '../lib/calendrier-utils';

// La grille de saisie est la vue annuelle du dossier, rendue modifiable : même
// palette, mêmes blocs de mois, même colonne « Sem. » que le « Calendrier de
// formation » remis à l'apprenant. La programmation et sa lecture ne racontent
// plus deux calendriers différents.

interface Props {
  /** Tous les jours de la période programmée, toutes années confondues. */
  jours: JourCalendrierLocal[];
  setJours: React.Dispatch<React.SetStateAction<JourCalendrierLocal[]>>;
  /** Année scolaire affichée : septembre `anneeDebut` → août `anneeDebut + 1`. */
  anneeDebut: number;
  /** Export PDF de **toute** la période, composé par le serveur. */
  onExporter?: () => void;
  exportEnCours?: boolean;
  lectureSeule?: boolean;
}

export default function CalendrierAdmin({
  jours,
  setJours,
  anneeDebut,
  onExporter,
  exportEnCours = false,
  lectureSeule = false,
}: Props) {
  const [selectedColor, setSelectedColor] = useState<TypeJour>('CENTRE_FORMATION');

  const handleApplyFeries = () => {
    setJours((prev) => appliquerJoursFeries(prev));
  };

  const handleApplyRythme = () => {
    // Exemple : CFA Lundi (1) et Mardi (2)
    const joursCFA = [1, 2];
    setJours((prev) => appliquerRythmeAlternance(prev, joursCFA));
  };

  const peindre = useCallback(
    (cible: Date) => {
      setJours((prev) =>
        prev.map((jour) =>
          jour.date.getTime() === cible.getTime() && jour.type !== selectedColor
            ? { ...jour, type: selectedColor }
            : jour,
        ),
      );
    },
    [setJours, selectedColor],
  );

  // Seuls les jours de l'année scolaire affichée, pour que la légende chiffrée
  // compte ce que l'on voit.
  const joursDeLAnnee = useMemo(() => {
    const debut = new Date(anneeDebut, 8, 1).getTime();
    const fin = new Date(anneeDebut + 1, 7, 31).getTime();
    return jours.filter((jour) => jour.date.getTime() >= debut && jour.date.getTime() <= fin);
  }, [jours, anneeDebut]);

  return (
    <div className="flex flex-col gap-6">
      {/* Barre d'outils et automatisations */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {!lectureSeule && (
            <>
              <button
                type="button"
                onClick={handleApplyFeries}
                className="min-h-[40px] rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                Appliquer Fériés
              </button>
              <button
                type="button"
                onClick={handleApplyRythme}
                className="min-h-[40px] rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                Rythme CFA (Lun/Mar)
              </button>
            </>
          )}
          {onExporter ? (
            <button
              type="button"
              onClick={onExporter}
              disabled={exportEnCours}
              className="inline-flex min-h-[40px] items-center rounded-md bg-codialis-navy px-4 py-2 text-sm font-medium text-white hover:bg-codialis-deep disabled:cursor-wait disabled:opacity-60"
            >
              <FileDown className="mr-2 h-4 w-4" aria-hidden />
              {exportEnCours ? 'Création du PDF…' : 'Exporter en PDF (toute la période)'}
            </button>
          ) : null}
        </div>

        {!lectureSeule && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-2 text-sm font-medium text-gray-500">Pinceau :</span>
            {ORDRE_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setSelectedColor(type)}
                aria-pressed={selectedColor === type}
                className={`min-h-[32px] rounded-full px-3 py-1 text-xs font-semibold transition-all ${STYLES_JOUR[type].pastille} ${
                  selectedColor === type ? 'scale-105 ring-2 ring-codialis-navy' : 'opacity-80 hover:opacity-100'
                }`}
              >
                {STYLES_JOUR[type].libelle}
              </button>
            ))}
          </div>
        )}
      </div>

      <CalendrierAnnuel
        jours={joursDeLAnnee}
        anneeDebut={anneeDebut}
        {...(lectureSeule ? {} : { onPeindre: peindre })}
      />
    </div>
  );
}
